import { useQuery, type QueryClient } from '@tanstack/react-query';
import { createApiClient } from '../api/client';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { workspaceKey } from './workspaceQueryKey';

const ASSISTANT_READ = 'assistant:read';

/**
 * The code on the façade's 403 when `POST /assistant/query` refuses a caller without
 * `assistant:read` (steward-app#1814). Mirrors `ASSISTANT_NOT_AVAILABLE_CODE` in
 * `services/steward-app-api/src/handlers/assistant.ts`; the SPA does not import the API.
 */
export const ASSISTANT_NOT_AVAILABLE_CODE = 'ASSISTANT_NOT_AVAILABLE';

/** The query key this hook reads, so a server refusal can write its answer into it. */
export function assistantCapabilityKey(workspaceId: string | null): readonly unknown[] {
  return workspaceKey('capabilities', workspaceId, ASSISTANT_READ);
}

/**
 * Record a server refusal as this hook's answer for `workspaceId`, so every surface the hook
 * drives (the `/ask` page, the top-bar control, the docked panel) moves onto the existing
 * `AssistantUnavailable` path instead of showing the refusal as a chat error.
 *
 * This is how the hook's fail-OPEN window closes: if the probe was in flight or errored when
 * the question was sent, the server (which fails CLOSED) is the one that answers, and its
 * answer is the same `false` the probe would have given.
 *
 * Only ever called for {@link ASSISTANT_NOT_AVAILABLE_CODE}, which the server sends only
 * when appcore ANSWERED `false`. When the server could not get an answer it sends 503
 * `ASSISTANT_CHECK_FAILED` instead, and `AssistantProvider` treats that as an ordinary
 * retryable chat error, so a transient failure never lands a family member here. That is
 * why this does not re-ask the probe: the value written is appcore's own answer, not a guess.
 */
export function markAssistantUnavailable(qc: QueryClient, workspaceId: string | null): void {
  qc.setQueryData(assistantCapabilityKey(workspaceId), { capabilities: { [ASSISTANT_READ]: false } });
}

/**
 * Whether the signed-in user may use the assistant in the ACTIVE workspace — the switch
 * behind the top-bar Ask control, the docked panel, and the `/ask` page (steward-app#932
 * phase 2).
 *
 * The same shape as `./useCanReadCredentials.ts`, and for the same reasons:
 * `GET /me/capabilities`, a thin façade passthrough
 * (`services/steward-app-api/src/handlers/capabilities.ts`) to appcore's own
 * `GET /w/{ws}/me/capabilities`, which answers through the SAME `evaluateRoleGrant` call
 * appcore's enforced gate makes on every `chat-session` read. Read that hook's header
 * before changing this one.
 *
 * ## Why this probes ONE string, where it used to probe two (steward-app#1254)
 *
 * It used to also probe `mem:manage` and return `true` on it, because appcore's enforced
 * screen short-circuited on break-glass before it read any `readCapability`, while its
 * capability probe had no such branch — so the two disagreed for admins, and probing the
 * string alone would have taken the assistant away from a workspace owner the API serves.
 *
 * appcore#603 retired that disagreement, and #605 deployed it — in ALL THREE environments,
 * which is the half worth stating, because "is prod on it?" is the question that would
 * otherwise tempt someone to put the second probe back.
 *
 * `GET /version` on each appcore API, read on 2026-09-08 for steward-app#1254:
 * dev (`sivmx02y1l`) `0d18c788a2e3ddcb560bdc1202bd043121006844`; stage (`f6u9dzmoaf`) and
 * prod (`5vuwd8zg0k`) `rel-2026.09.08.4`, both reporting that same commit. It is also the
 * head of the local crossmarc/appcore `main`, where `UNCONDITIONAL_CAPABILITIES` in
 * `packages/core/src/auth/permissions.ts` is `Object.freeze(['*:*'])` — `mem:manage` is an
 * ordinary admin capability that short-circuits nothing, pinned in both directions by
 * `role.invariants.test.ts` and `unconditional-capabilities-is-exact-match.test.ts`.
 * ⚠️ That is a deploy reading with a date on it, not a property; re-read it rather than
 * trusting this line.
 *
 * ⚠️ **`mem:manage` did not stop mattering; it stopped mattering HERE.** It survives as
 * `ACL_ADMINISTRATOR_CAPABILITY`, which exempts its holder from Tier-3 ACL EXCLUSION —
 * you cannot be locked out of an ACL you administer. That constant's own docblock says,
 * first of three limits, that it does **not** satisfy a type-level `readCapability` or
 * `writeCapability`. `chat-session` is gated by exactly such a declaration
 * (`packages/steward-types/src/index.ts`, `assistantAccess.ts`), so the Tier-3 exemption
 * cannot reach it, and the probe and the enforced gate now answer the same question.
 * Do not reintroduce a second probed string on the strength of the word "break-glass"
 * appearing in an ACL comment elsewhere in this repo: those are the Tier-3 statement,
 * which is still true, and this was the Tier-1 one, which is not.
 *
 * The population this changes the answer for is a membership holding appcore's
 * `workspace-admin` (`mem:manage`, `inst:*`) and no steward-* role — the raw-API shape.
 * appcore now refuses their `chat-session` read, so offering them the surface was
 * fail-OPEN against the deployed gate: chat that answers and then forgets every turn,
 * which is the exact failure the founder's 2026-08-27 ruling on #932 says to refuse in
 * plain copy instead. A founder who also holds `steward-admin` is unaffected, because
 * that role spells `assistant:read` out (`packRoles.ts`) rather than relying on a bypass.
 *
 * ⚠️ **UX GATING ONLY, NEVER AUTHORIZATION.** `chat-session` declares
 * `accessControl.readCapability: 'assistant:read'`
 * (`packages/steward-types/src/index.ts`, `assistantAccess.ts`), and THAT is what refuses
 * an advisor's read of a family's transcripts, including through appcore's generic entities
 * route, which this SPA is not in the path of. One honest exception: appcore's SigV4
 * `/system/*` plane runs no capability screen at all (`access.ts` says so and asks callers
 * not to claim otherwise). Nothing there touches `chat-session` today, so the claim is
 * "your advisor cannot see this", never "nothing can".
 *
 * That declaration protects the TRANSCRIPTS. Asking a question is refused separately, by
 * the façade: `POST /assistant/query` probes this same string before any read, budget check
 * or model call, and FAILS CLOSED (steward-app#1814, `requireAssistantRead` in
 * `services/steward-app-api/src/handlers/assistant.ts`). An answered `false` comes back as a
 * 403 carrying {@link ASSISTANT_NOT_AVAILABLE_CODE}; a probe it could not complete comes back
 * as a 503 `ASSISTANT_CHECK_FAILED`, which is an ordinary chat error and moves nothing here.
 * On the 403, `AssistantProvider` writes that refusal into
 * this hook's cache ({@link markAssistantUnavailable}), so a send made during this hook's
 * fail-open window still lands on `AssistantUnavailable`.
 *
 * ## Why this exists at all, given it protects nothing
 *
 * Because arming binds WRITES too (appcore adds `readCapability` under both intents), and
 * the failure mode without this hook is not an error a user could report. Trace it in
 * `services/steward-app-api/src/handlers/assistant.ts`: the create fails inside the
 * best-effort `try`, so `persistedThreadId` stays the incoming (undefined) `threadId`;
 * `loadThread` swallows its own failure and returns `null`, so `priorMessages` is empty.
 * An advisor in a client workspace would keep getting answers while the assistant forgot
 * the previous message every single turn, with `GET /assistant/threads` a cheerful empty
 * 200 and nothing surfaced anywhere. The founder's 2026-08-27 ruling on #932 is that the
 * surface is refused with plain copy instead. So this hook is honesty, not protection.
 *
 * ⚠️ It closes that failure mode for `steward-advisor` ONLY. `steward-viewer` HOLDS
 * `assistant:read`, so this gate passes them straight through. Before #590 their persist
 * then 403'd into the same swallowing `try` (the type demanded a write grant the viewer
 * lacks). That is fixed at the type, not here: appcore rel-2026.10.26 added creator
 * ownership, and steward-app arms it on `chat-session` from the release that carries #590,
 * after which appcore lets an `inst:read` + `assistant:read` holder create their own
 * conversation. Whether an environment is armed is verified at source, per environment. This hook played no part in it,
 * and must not be widened or narrowed on the strength of #590.
 *
 * ## Fails OPEN on an UNANSWERED probe, and that asymmetry is deliberate
 *
 * While loading, on any error, and with no active workspace, this returns `true` —
 * matching `useCanReadCredentials`. Hiding the assistant from a family member who is
 * entitled to it is the costly mistake here; the cost of a false `true` is a chat surface
 * whose sends fail visibly, and appcore refuses the transcript read either way. Note the
 * consequence: a refused principal sees the chat surface for the moment the probe is in
 * flight, then the refusal. Better that way round than a family member watching their own
 * assistant disappear on a slow network.
 *
 * That is fail-open on a probe that did not ANSWER, which is a narrower thing than the
 * fail-open the paragraph above retired: an answered `assistant:read: false` is now a
 * denial with nothing to override it.
 */
export function useCanUseAssistant(): boolean {
  const token = useAccessToken();
  const { activeWorkspaceId } = useWorkspace();

  const { data, isLoading, isError } = useQuery({
    queryKey: assistantCapabilityKey(activeWorkspaceId),
    queryFn: () =>
      createApiClient(token!).get<{ capabilities: Record<string, boolean> }>(
        `/me/capabilities?capabilities=${encodeURIComponent(ASSISTANT_READ)}`,
      ),
    enabled: !!token && !!activeWorkspaceId,
    staleTime: 5 * 60_000,
  });

  if (!activeWorkspaceId || isLoading || isError) return true;
  // `=== false`, not `!`: an absent key or a non-boolean value must not read as a denial
  // here, matching `useCanReadCredentials` and the server-side care in `writeGate.ts`.
  return data?.capabilities?.[ASSISTANT_READ] !== false;
}
